import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Award, ThumbsUp, TrendingUp, RotateCcw, Star, FileDown, Loader2 } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SaveToEvidence from "@/components/SaveToEvidence";

const GRADE_TONE = {
  Outstanding: "bg-accent text-white", Good: "bg-emerald-600 text-white",
  "Requires Improvement": "bg-amber-500 text-white", Inadequate: "bg-destructive text-white",
};

export default function InterviewReport({ interview, onRestart, canRestart }) {
  const r = interview.report || {};
  const comps = Object.entries(r.competency_scores || {});
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/interviews/${interview.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${interview.regulator}-mock-interview-report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Could not generate the PDF — please try again"); }
    finally { setDownloading(false); }
  };
  return (
    <div className="space-y-6" data-testid="interview-report">
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        <div className="grid md:grid-cols-3">
          <div className="p-8 bg-primary text-primary-foreground flex flex-col items-center justify-center text-center">
            <Award className="w-8 h-8 mb-3 opacity-90" />
            <p className="font-display text-6xl font-extrabold leading-none" data-testid="report-overall-score">{r.overall_score}<span className="text-2xl">%</span></p>
            <Badge className={`mt-4 border-0 ${GRADE_TONE[r.grade] || "bg-white/20 text-white"}`} data-testid="report-grade">{r.grade}</Badge>
            <p className="text-xs opacity-75 mt-3">{interview.regulator} readiness · {new Date(interview.completed_at).toLocaleDateString("en-GB")}</p>
          </div>
          <div className="md:col-span-2 p-8">
            <h2 className="font-display font-bold text-lg mb-2">Inspector's summary</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6" data-testid="report-summary">{r.summary}</p>
            <div className="space-y-3">
              {comps.map(([name, score]) => (
                <div key={name} data-testid="report-competency-row">
                  <div className="flex justify-between text-sm mb-1"><span>{name}</span><span className="font-semibold">{score}/10</span></div>
                  <Progress value={Number(score) * 10} className="h-2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <h3 className="font-display font-bold flex items-center gap-2 mb-3"><ThumbsUp className="w-5 h-5 text-accent" /> Strengths</h3>
          <ul className="space-y-2">
            {(r.strengths || []).map((s, i) => <li key={`s-${s.slice(0, 40)}-${i}`} className="flex gap-2 text-sm"><Star className="w-4 h-4 text-accent mt-0.5 shrink-0" /> {s}</li>)}
          </ul>
        </div>
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <h3 className="font-display font-bold flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5 text-amber-500" /> Work on next</h3>
          <ul className="space-y-2">
            {(r.improvements || []).map((s, i) => <li key={`i-${s.slice(0, 40)}-${i}`} className="flex gap-2 text-sm"><span className="w-5 h-5 rounded-full bg-amber-500/10 text-amber-600 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span> {s}</li>)}
          </ul>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
        <h3 className="font-display font-bold mb-4">Question-by-question</h3>
        <div className="space-y-4">
          {interview.turns.map((t, i) => (
            <div key={`${interview.id}-q${i + 1}`} className="border-l-2 border-border pl-4">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium">Q{i + 1}. {t.question}</p>
                <Badge variant="secondary" className="shrink-0">{t.score}/10</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1.5 whitespace-pre-wrap">{t.answer}</p>
              <p className="text-xs text-accent mt-1.5">{t.feedback}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 flex-wrap">
        <SaveToEvidence sourceType="interview_report" refId={interview.id} regulator={interview.regulator}
          defaultTitle={`${interview.regulator} mock interview report — ${r.grade || ""} ${r.overall_score ?? ""}%`} />
        <Button variant="outline" onClick={downloadPdf} disabled={downloading} data-testid="interview-download-pdf-button">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileDown className="w-4 h-4 mr-1.5" />} Download report PDF
        </Button>
        {canRestart && (
          <Button onClick={onRestart} data-testid="interview-restart-button"><RotateCcw className="w-4 h-4 mr-1.5" /> Practise again</Button>
        )}
      </div>
    </div>
  );
}
